import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { CreditCard, RefreshCw, Repeat } from 'lucide-react'
import {
  NavigationLink,
  Button,
  Card,
  Chip,
  DetailHero,
  Drawer,
  DrawerTabPanel,
  DrawerTabs,
  EmptyState,
  ErrorState,
  FilterToolbar,
  LoadMore,
  KeyValueGrid,
  MoneyValue,
  SearchField,
  Skeleton,
  StatusBadge,
  TechnicalDetails,
  TextField,
} from '@shared/design-system/ui'
import { SubscriptionStatusSchema } from '@shared/contracts/enums'
import type { SubscriptionStatus } from '@shared/contracts/enums'
import { paymentMethodLabel } from '@entities/sale'
import { useDebounce } from '@shared/lib/use-debounce'
import { useMediaQuery } from '@shared/lib/use-media-query'
import { useSession } from '@shared/lib/auth/session-store'
import { hasAdminPermission } from '@shared/lib/auth/permissions'
import { toast } from '@shared/lib/toast/toast-store'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import {
  alignRight,
  buyerCell as mainCell,
  buyerName as primary,
  buyerProduct as secondary,
  clickableRow as row,
  filterControls as controls,
  header,
  hideOnMobile,
  ledgerReference,
  ledgerReferenceLead,
  ledgerReferenceText,
  ledgerReferenceTitle,
  mobileCard,
  mobileCardHeader,
  mobileCardLead,
  mobileList,
  mobileMetaGrid,
  mobileMetaItem,
  mobileMetaLabel,
  mobileMetaValue,
  page,
  resultCount,
  saleDetail,
  saleChargeTag,
  saleLineHint,
  saleMetaGrid,
  saleMetaItem,
  saleMetaLabel,
  saleMetaValue,
  saleSection,
  saleSectionTitle,
  saleSoftNote,
  statusOption,
  subtitle,
  table,
  tableCard,
  tableViewport,
  td,
  th,
  title,
  titleBlock,
  viewTabs,
} from './admin.css'
import {
  cancelAdminSubscription,
  exportAdminSubscriptions,
  useAdminRuntimeCapabilities,
  useAdminSubscriptionsInfinite,
  useAdminUsersLookup,
} from '../api/admin.api'
import type { ExportFormat, SubscriptionItem } from '../api/admin.api'
import { downloadBlob, exportTimestamp } from '../lib/download-xlsx'
import { ExportButton } from './ExportButton'
import { AdminSaleDetailDrawer } from './AdminSaleDetailDrawer'
import { AdminSubscriptionTestPanel } from './AdminSubscriptionTestPanel'
import { AdminDashboardFilters } from './AdminDashboardFilters'
import type { DashboardSelection } from './AdminDashboardFilters'
import { adminListPeriodRange, dashboardRangeLabel } from '../lib/admin-dashboard-period'

import { adminUserDisplay } from './admin-user-display'

const statusOptions = SubscriptionStatusSchema.options

function commercialAmountOf(subscription: SubscriptionItem) {
  return subscription.commercialAmount
}

function hasInitialSale(subscription: SubscriptionItem) {
  return subscription.initialSaleAvailable ?? true
}

function statusPresentation(status: string) {
  if (status === 'ACTIVE') return { label: 'Ativa', tone: 'success' as const }
  if (status === 'TRIALING') return { label: 'Trial', tone: 'informative' as const }
  if (status === 'PAST_DUE') return { label: 'Em atraso', tone: 'awaiting' as const }
  return { label: 'Cancelada', tone: 'closed' as const }
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(value))
}

function gatewayLabel(value: string) {
  if (value === 'STRIPE') return 'Stripe'
  if (value === 'MERCADO_PAGO') return 'Mercado Pago'
  if (value === 'PAYPAL') return 'PayPal'
  return humanizeConstant(value)
}

function humanizeConstant(value: string) {
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

function buyerLabel(subscription: SubscriptionItem) {
  return subscription.buyerName ?? subscription.buyerEmail ?? 'Comprador sem nome'
}

function participationLabel(subscription: SubscriptionItem) {
  const participants: string[] = []
  if (subscription.affiliateId) {
    participants.push(
      subscription.affiliateRecurring === false
        ? 'afiliado somente na primeira cobrança'
        : subscription.affiliateRecurring === true
          ? 'afiliado recorrente'
          : 'afiliado: recorrência não informada',
    )
  }
  if (subscription.coProducerId) participants.push('coprodutor recorrente')
  if (subscription.seedId) {
    participants.push(
      `indicação${subscription.seedPercentage ? ` ${subscription.seedPercentage}%` : ''}`,
    )
  }
  return participants.length > 0 ? `Com ${participants.join(' · ')}` : 'Somente produtor'
}

function participationExplanation(subscription: SubscriptionItem) {
  const label = participationLabel(subscription)
  if (!subscription.affiliateId) {
    return `${label}. Coprodução e indicação seguem o snapshot da assinatura.`
  }
  if (subscription.affiliateId && subscription.affiliateRecurring === undefined) {
    return `${label}. A API administrativa não informou se o afiliado participa das renovações; nenhuma regra financeira foi inferida. Coprodução e indicação seguem o snapshot da assinatura.`
  }
  return `${label}. A recorrência do afiliado segue o snapshot da assinatura. Coprodução e indicação também seguem o snapshot.`
}

function nextBillingPresentation(subscription: SubscriptionItem) {
  if (subscription.status === 'CANCELED') {
    return { value: 'Sem próxima cobrança', detail: 'Cancelada' }
  }
  if (subscription.cancelAtPeriodEnd) {
    return {
      value: formatDate(subscription.currentPeriodEndAt),
      detail: 'Encerra no fim do período',
    }
  }
  return {
    value: formatDate(subscription.currentPeriodEndAt),
    detail: 'Renovação automática',
  }
}

function DebouncedSearchInput({
  value,
  onCommit,
}: {
  value: string
  onCommit: (value: string) => void
}) {
  const [text, setText] = useState(value)
  const debounced = useDebounce(text, 300)

  useEffect(() => {
    onCommit(debounced.trim())
  }, [debounced, onCommit])

  return (
    <SearchField
      value={text}
      onChange={setText}
      placeholder="Buscar por assinatura, venda inicial ou comprador"
      ariaLabel="Buscar assinaturas"
    />
  )
}

function SubscriptionDrawer({
  subscription,
  usersById,
  usersLoading = false,
  onClose,
  onViewSale,
}: {
  subscription: SubscriptionItem | null
  usersById: ReturnType<typeof useAdminUsersLookup>['usersById']
  usersLoading?: boolean
  onClose: () => void
  onViewSale: (saleId: string) => void
}) {
  const [cancellationReason, setCancellationReason] = useState('')
  const [activeTab, setActiveTab] = useState('summary')
  const queryClient = useQueryClient()
  const currentUser = useSession((state) => state.user)
  const canManage = hasAdminPermission(currentUser, 'SUBSCRIPTION_MANAGE')
  const canTestSubscription = hasAdminPermission(currentUser, 'QA_TOOLS')
  const runtimeCapabilities = useAdminRuntimeCapabilities(
    canTestSubscription && subscription !== null,
  )
  const subscriptionTestEnabled =
    canTestSubscription && runtimeCapabilities.data?.qaToolsEnabled === true
  const cancelMutation = useMutation({
    mutationFn: (input: { subscriptionId: string; reason: string }) =>
      cancelAdminSubscription(input.subscriptionId, { reason: input.reason }),
    onSuccess: () => {
      toast.success('Cancelamento agendado para o fim do período.')
      setCancellationReason('')
      void queryClient.invalidateQueries({ queryKey: ['admin-subscriptions-infinite'] })
    },
    onError: (error) => {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível cancelar a assinatura',
          fallback: 'Confira o motivo e tente novamente.',
        }),
      )
    },
  })
  function closeDrawer() {
    setCancellationReason('')
    setActiveTab('summary')
    onClose()
  }
  const initialSaleAvailable = subscription ? hasInitialSale(subscription) : false
  const producerDisplay = subscription
    ? adminUserDisplay({
        userId: subscription.producerId,
        usersById,
        isLoading: usersLoading,
      })
    : null
  const affiliateDisplay = subscription?.affiliateId
    ? adminUserDisplay({
        userId: subscription.affiliateId,
        usersById,
        isLoading: usersLoading,
      })
    : null
  const coProducerDisplay = subscription?.coProducerId
    ? adminUserDisplay({
        userId: subscription.coProducerId,
        usersById,
        isLoading: usersLoading,
      })
    : null
  const seedDisplay = subscription?.seedId
    ? adminUserDisplay({
        userId: subscription.seedId,
        usersById,
        isLoading: usersLoading,
      })
    : null
  const recurringCommercial = subscription ? commercialAmountOf(subscription) : undefined
  const buyerCharge = subscription?.chargeAmount
  const tabs = [
    { id: 'summary', label: 'Resumo' },
    { id: 'participants', label: 'Participantes' },
    { id: 'links', label: 'Vínculos' },
    ...(subscriptionTestEnabled ? [{ id: 'test', label: 'Teste QA' }] : []),
    { id: 'technical', label: 'Técnico' },
  ]
  const technicalRows = subscription
    ? [
        { label: 'Assinatura', value: subscription.id },
        { label: 'Plano', value: subscription.planId },
        { label: 'Produto', value: subscription.productId },
        ...(initialSaleAvailable
          ? [{ label: 'Venda inicial', value: subscription.initialSaleId }]
          : []),
      ]
    : []

  return (
    <Drawer
      open={subscription !== null}
      onClose={closeDrawer}
      title={subscription ? buyerLabel(subscription) : 'Assinatura'}
      description={
        subscription?.buyerEmail ?? 'Ciclo, pagamento, participantes, vínculos e referências.'
      }
      size="wide"
      variant="detail"
      busy={cancelMutation.isPending}
      dirty={cancellationReason.trim().length > 0}
      status={
        subscription ? (
          <>
            <StatusBadge
              tone={statusPresentation(subscription.status).tone}
              label={statusPresentation(subscription.status).label}
            />
            {subscription.cancelAtPeriodEnd ? (
              <span className={saleChargeTag}>Encerra no fim do período</span>
            ) : null}
          </>
        ) : undefined
      }
      tabs={
        subscription ? (
          <DrawerTabs
            tabs={tabs}
            value={activeTab}
            onChange={setActiveTab}
            ariaLabel="Seções da assinatura"
          />
        ) : undefined
      }
    >
      {subscription ? (
        <div className={saleDetail}>
          <DrawerTabPanel id="summary" active={activeTab === 'summary'}>
            <DetailHero
              eyebrow="Preço contratado recorrente"
              value={
                recurringCommercial ? (
                  <MoneyValue
                    amount={recurringCommercial.amount}
                    currency={recurringCommercial.currency}
                    size="lg"
                  />
                ) : (
                  'Valor histórico não disponível'
                )
              }
              description={`Criada em ${formatDate(subscription.createdAt)} por ${gatewayLabel(subscription.gateway)}.`}
              aside={
                <span>
                  Próxima cobrança
                  <br />
                  <strong>{nextBillingPresentation(subscription).value}</strong>
                </span>
              }
            />
            <KeyValueGrid
              items={[
                {
                  label: 'Cobrança ao comprador',
                  value: buyerCharge ? (
                    <>
                      <MoneyValue amount={buyerCharge.amount} currency={buyerCharge.currency} />
                      <span className={saleLineHint}>Valor na moeda escolhida pelo comprador</span>
                    </>
                  ) : (
                    'Não informada'
                  ),
                },
                {
                  label: 'Forma de pagamento',
                  value: `${paymentMethodLabel(subscription.paymentMethod)} · ${gatewayLabel(subscription.gateway)}`,
                },
                { label: 'Próxima cobrança', value: nextBillingPresentation(subscription).value },
                { label: 'Modelo', value: participationLabel(subscription) },
              ]}
            />
          </DrawerTabPanel>

          <DrawerTabPanel id="participants" active={activeTab === 'participants'}>
            <div className={saleSection}>
              <span className={saleSectionTitle}>Participação recorrente</span>
              <div className={saleSoftNote}>
                <span>{participationExplanation(subscription)}</span>
              </div>
              <div className={saleMetaGrid}>
                <div className={saleMetaItem}>
                  <span className={saleMetaLabel}>Modelo</span>
                  <span className={saleMetaValue}>{participationLabel(subscription)}</span>
                </div>
                <div className={saleMetaItem}>
                  <span className={saleMetaLabel}>Produtor</span>
                  <span className={saleMetaValue}>
                    {producerDisplay?.primary ?? 'E-mail não localizado'}
                  </span>
                  {producerDisplay?.secondary ? (
                    <span className={saleLineHint}>{producerDisplay.secondary}</span>
                  ) : null}
                </div>
                {affiliateDisplay ? (
                  <div className={saleMetaItem}>
                    <span className={saleMetaLabel}>Afiliado</span>
                    <span className={saleMetaValue}>{affiliateDisplay.primary}</span>
                    {affiliateDisplay.secondary ? (
                      <span className={saleLineHint}>{affiliateDisplay.secondary}</span>
                    ) : null}
                  </div>
                ) : null}
                {coProducerDisplay ? (
                  <div className={saleMetaItem}>
                    <span className={saleMetaLabel}>Coprodutor</span>
                    <span className={saleMetaValue}>{coProducerDisplay.primary}</span>
                    {coProducerDisplay.secondary ? (
                      <span className={saleLineHint}>{coProducerDisplay.secondary}</span>
                    ) : null}
                  </div>
                ) : null}
                {seedDisplay ? (
                  <div className={saleMetaItem}>
                    <span className={saleMetaLabel}>Indicação</span>
                    <span className={saleMetaValue}>{seedDisplay.primary}</span>
                    <span className={saleLineHint}>
                      {subscription.seedPercentage
                        ? `${subscription.seedPercentage}% por ciclo`
                        : 'Percentual não informado'}
                      {seedDisplay.secondary ? ` · ${seedDisplay.secondary}` : ''}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </DrawerTabPanel>

          <DrawerTabPanel id="links" active={activeTab === 'links'}>
            <div className={ledgerReference}>
              <div className={ledgerReferenceLead}>
                <span className={ledgerReferenceTitle}>
                  {initialSaleAvailable ? 'Venda inicial vinculada' : 'Sem venda inicial vinculada'}
                </span>
                <span className={ledgerReferenceText}>
                  {initialSaleAvailable
                    ? 'Abra a venda para conferir cobrança, taxas, participantes e lançamentos financeiros.'
                    : 'Esta assinatura começou com período gratuito e não possui venda inicial. Quando houver cobranças, elas aparecem separadamente como renovações na lista de vendas.'}
                </span>
              </div>
              {initialSaleAvailable ? (
                <NavigationLink onClick={() => onViewSale(subscription.initialSaleId)}>
                  Ver venda
                </NavigationLink>
              ) : null}
            </div>

            {subscription.status !== 'CANCELED' && !subscription.cancelAtPeriodEnd ? (
              <div className={saleSection}>
                <span className={saleSectionTitle}>Override administrativo</span>
                <div className={saleSoftNote}>
                  Agenda o cancelamento para o fim do período atual e registra motivo e operador na
                  auditoria.
                </div>
                <div className={saleMetaItem}>
                  <span className={saleMetaLabel}>Cancelará em</span>
                  <span className={saleMetaValue}>
                    {formatDate(subscription.currentPeriodEndAt)}
                  </span>
                  <span className={saleLineHint}>
                    A assinatura permanece ativa até o fim do período atual.
                  </span>
                </div>
                <TextField
                  label="Motivo da intervenção"
                  value={cancellationReason}
                  maxLength={500}
                  onChange={(event) => setCancellationReason(event.currentTarget.value)}
                />
                <Button
                  variant="danger"
                  size="sm"
                  disabled={
                    !canManage || cancellationReason.trim().length < 3 || cancelMutation.isPending
                  }
                  onClick={() =>
                    cancelMutation.mutate({
                      subscriptionId: subscription.id,
                      reason: cancellationReason.trim(),
                    })
                  }
                >
                  Cancelar no fim do período
                </Button>
                {!canManage ? (
                  <span className={saleLineHint}>
                    Seu perfil não possui a permissão SUBSCRIPTION_MANAGE.
                  </span>
                ) : null}
              </div>
            ) : subscription.cancelAtPeriodEnd ? (
              <div className={saleSection}>
                <span className={saleSectionTitle}>Cancelamento agendado</span>
                <div className={saleMetaItem}>
                  <span className={saleMetaLabel}>Cancelará em</span>
                  <span className={saleMetaValue}>
                    {formatDate(subscription.currentPeriodEndAt)}
                  </span>
                  <span className={saleLineHint}>
                    A assinatura será encerrada automaticamente no fim do período atual.
                  </span>
                </div>
              </div>
            ) : null}
          </DrawerTabPanel>

          {subscriptionTestEnabled ? (
            <DrawerTabPanel id="test" active={activeTab === 'test'}>
              <AdminSubscriptionTestPanel
                key={subscription.id}
                subscriptionId={subscription.id}
                active={activeTab === 'test'}
              />
            </DrawerTabPanel>
          ) : null}

          <DrawerTabPanel id="technical" active={activeTab === 'technical'}>
            <TechnicalDetails rows={technicalRows} defaultOpen />
          </DrawerTabPanel>
        </div>
      ) : null}
    </Drawer>
  )
}

function SubscriptionCards({
  items,
  onSelect,
}: {
  items: SubscriptionItem[]
  onSelect: (subscription: SubscriptionItem) => void
}) {
  return (
    <div className={mobileList}>
      {items.map((subscription) => {
        const presentation = statusPresentation(subscription.status)
        const nextBilling = nextBillingPresentation(subscription)
        const recurringCommercial = commercialAmountOf(subscription)
        return (
          <div
            key={subscription.id}
            className={mobileCard}
            role="button"
            tabIndex={0}
            aria-label={`Ver assinatura de ${buyerLabel(subscription)}`}
            onClick={() => onSelect(subscription)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onSelect(subscription)
              }
            }}
          >
            <div className={mobileCardHeader}>
              <span className={mobileCardLead}>
                <span className={primary}>{buyerLabel(subscription)}</span>
                <span className={secondary}>
                  {subscription.buyerEmail ?? 'E-mail do comprador não informado'}
                </span>
              </span>
              <StatusBadge tone={presentation.tone} label={presentation.label} size="sm" />
            </div>
            <div className={mobileMetaGrid}>
              <span className={mobileMetaItem}>
                <span className={mobileMetaLabel}>Próxima cobrança</span>
                <span className={mobileMetaValue}>{nextBilling.value}</span>
                <span className={secondary}>{nextBilling.detail}</span>
              </span>
              <span className={mobileMetaItem}>
                <span className={mobileMetaLabel}>Preço contratado</span>
                <span className={mobileMetaValue}>
                  {recurringCommercial ? (
                    <MoneyValue
                      amount={recurringCommercial.amount}
                      currency={recurringCommercial.currency}
                      size="sm"
                    />
                  ) : (
                    subscription.planId
                  )}
                </span>
                <span className={secondary}>
                  <RefreshCw size={12} /> {participationLabel(subscription)}
                </span>
              </span>
              <span className={mobileMetaItem}>
                <span className={mobileMetaLabel}>Pagamento</span>
                <span className={mobileMetaValue}>
                  <CreditCard size={14} /> {gatewayLabel(subscription.gateway)}
                </span>
                <span className={secondary}>{paymentMethodLabel(subscription.paymentMethod)}</span>
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function AdminSubscriptionsScreen() {
  const [q, setQ] = useState('')
  const [selection, setSelection] = useState<DashboardSelection>({ period: 'today' })
  const range = useMemo(
    () =>
      selection.period === 'custom'
        ? (selection.custom ?? {})
        : adminListPeriodRange(selection.period),
    [selection],
  )
  const [status, setStatus] = useState<SubscriptionStatus | undefined>()
  const [selected, setSelected] = useState<SubscriptionItem | null>(null)
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null)
  const isMobile = useMediaQuery('(max-width: 720px)')

  const subscriptions = useAdminSubscriptionsInfinite({
    ...range,
    ...(q ? { q } : {}),
    ...(selection.user?.id ? { participantId: selection.user?.id } : {}),
    ...(status ? { status } : {}),
  })
  const items = useMemo(
    () => subscriptions.data?.pages.flatMap((page) => page.items) ?? [],
    [subscriptions.data?.pages],
  )
  const participantIds = useMemo(() => {
    const ids = new Set<string>()
    for (const item of items) {
      ids.add(item.producerId)
      if (item.affiliateId) ids.add(item.affiliateId)
      if (item.coProducerId) ids.add(item.coProducerId)
      if (item.seedId) ids.add(item.seedId)
    }
    if (selected?.producerId) ids.add(selected.producerId)
    return [...ids]
  }, [items, selected])
  const participantLookup = useAdminUsersLookup(participantIds)
  const filtersActive = Boolean(q) || Boolean(selection.user?.id) || Boolean(status)

  async function handleExport(format: ExportFormat) {
    const params = {
      ...range,
      ...(q ? { q } : {}),
      ...(selection.user?.id ? { participantId: selection.user.id } : {}),
      ...(status ? { status } : {}),
    }
    const blob = await exportAdminSubscriptions(params, format)
    downloadBlob(blob, `assinaturas-${exportTimestamp()}.${format}`)
  }

  const chips: ReactNode[] = []
  if (q) {
    chips.push(<Chip key="q" label={`Busca: ${q}`} onRemove={() => setQ('')} />)
  }
  if (selection.user?.id) {
    chips.push(
      <Chip
        key="user"
        label={`Usuário: ${selection.user?.name || selection.user?.email}`}
        onRemove={() => {
          setSelection({ ...selection, user: undefined })
        }}
      />,
    )
  }

  return (
    <section className={page} data-testid="subscriptions-screen">
      <div className={header}>
        <div className={titleBlock}>
          <h1 className={title}>Assinaturas</h1>
          <p className={subtitle}>
            Renovações, atrasos e participações recorrentes em visão administrativa global.
          </p>
        </div>
        <ExportButton onExport={handleExport} />
      </div>

      <AdminDashboardFilters selection={selection} onApply={setSelection} />
      <p className={subtitle}>
        Assinaturas criadas: {dashboardRangeLabel(range)}.{' '}
        {selection.user
          ? `Usuário: ${selection.user.name || selection.user.email}`
          : 'Todos os usuários'}
        .
      </p>

      <Card padding="md" className={tableCard}>
        <div className={viewTabs} role="group" aria-label="Filtrar assinaturas por status">
          <button
            type="button"
            className={statusOption({ active: status === undefined })}
            aria-pressed={status === undefined}
            onClick={() => setStatus(undefined)}
          >
            Todas
          </button>
          {statusOptions.map((option) => (
            <button
              key={option}
              type="button"
              className={statusOption({ active: status === option })}
              aria-pressed={status === option}
              onClick={() => setStatus(status === option ? undefined : option)}
            >
              {statusPresentation(option).label}
            </button>
          ))}
        </div>

        <FilterToolbar
          chips={chips.length > 0 ? chips : null}
          onClear={
            filtersActive
              ? () => {
                  setQ('')
                  setStatus(undefined)
                  setSelection({ ...selection, user: undefined })
                }
              : undefined
          }
          resultCount={
            <span className={resultCount}>
              {items.length}{' '}
              {items.length === 1 ? 'assinatura carregada' : 'assinaturas carregadas'}
            </span>
          }
        >
          <div className={controls} role="group" aria-label="Filtrar assinaturas">
            <DebouncedSearchInput key={q} value={q} onCommit={setQ} />
          </div>
        </FilterToolbar>

        {subscriptions.isPending ? (
          <Skeleton height={320} />
        ) : subscriptions.isError ? (
          <ErrorState
            title={
              subscriptions.error.message === 'SUBSCRIPTION_DATE_FILTER_UNSUPPORTED'
                ? 'Filtro por data indisponível'
                : undefined
            }
            description={
              subscriptions.error.message === 'SUBSCRIPTION_DATE_FILTER_UNSUPPORTED'
                ? 'A consulta não confirmou o período solicitado. Nenhuma assinatura será exibida sem esse filtro.'
                : undefined
            }
            onRetry={() => {
              void subscriptions.refetch()
            }}
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Repeat size={22} />}
            title="Nenhuma assinatura encontrada"
            description="Ajuste a busca, o status ou o usuário para consultar outras assinaturas."
          />
        ) : (
          <>
            {isMobile ? (
              <SubscriptionCards items={items} onSelect={setSelected} />
            ) : (
              <div className={tableViewport}>
                <table className={table}>
                  <thead>
                    <tr>
                      <th className={th}>Comprador</th>
                      <th className={th}>Status</th>
                      <th className={`${th} ${hideOnMobile}`}>Próxima cobrança</th>
                      <th className={`${th} ${hideOnMobile}`}>Cartão</th>
                      <th className={`${th} ${alignRight}`}>Preço contratado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((subscription) => {
                      const presentation = statusPresentation(subscription.status)
                      const nextBilling = nextBillingPresentation(subscription)
                      const recurringCommercial = commercialAmountOf(subscription)
                      return (
                        <tr
                          key={subscription.id}
                          className={row}
                          onClick={() => setSelected(subscription)}
                        >
                          <td className={td}>
                            <span className={mainCell}>
                              <span className={primary}>{buyerLabel(subscription)}</span>
                              <span className={secondary}>
                                {subscription.buyerEmail ?? 'E-mail do comprador não informado'}
                              </span>
                            </span>
                          </td>
                          <td className={td}>
                            <StatusBadge
                              tone={presentation.tone}
                              label={presentation.label}
                              size="sm"
                            />
                          </td>
                          <td className={`${td} ${hideOnMobile}`}>
                            <span className={mainCell}>
                              <span className={primary}>{nextBilling.value}</span>
                              <span className={secondary}>{nextBilling.detail}</span>
                            </span>
                          </td>
                          <td className={`${td} ${hideOnMobile}`}>
                            <span className={mainCell}>
                              <span className={primary}>
                                <CreditCard size={14} /> {gatewayLabel(subscription.gateway)}
                              </span>
                              <span className={secondary}>
                                {paymentMethodLabel(subscription.paymentMethod)}
                              </span>
                            </span>
                          </td>
                          <td className={`${td} ${alignRight}`}>
                            <span className={mainCell}>
                              <span className={primary}>
                                {recurringCommercial ? (
                                  <MoneyValue
                                    amount={recurringCommercial.amount}
                                    currency={recurringCommercial.currency}
                                    size="sm"
                                  />
                                ) : (
                                  subscription.planId
                                )}
                              </span>
                              <span className={secondary}>
                                <RefreshCw size={12} /> {participationLabel(subscription)}
                              </span>
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <LoadMore
              shown={items.length}
              hasMore={subscriptions.hasNextPage}
              loading={subscriptions.isFetchingNextPage}
              onClick={() => {
                void subscriptions.fetchNextPage()
              }}
            />
          </>
        )}
      </Card>

      <SubscriptionDrawer
        key={selected?.id ?? 'closed'}
        subscription={selected}
        usersById={participantLookup.usersById}
        usersLoading={participantLookup.isLoading}
        onClose={() => setSelected(null)}
        onViewSale={(saleId) => {
          setSelected(null)
          setSelectedSaleId(saleId)
        }}
      />
      <AdminSaleDetailDrawer saleId={selectedSaleId} onClose={() => setSelectedSaleId(null)} />
    </section>
  )
}
